// OurStores.jsx
import React, {useRef, useState, useEffect} from 'react';
import './OurStores.css';
import img from '../assets/store.jpg';
import Footer from '../pages/Footer';
import FloatingOrderButton from '../components/FloatingOrderButton';
import GoogleMap from '../components/GoogleMap';
import { 
  FaChevronLeft, 
  FaChevronRight, 
  FaMapMarkerAlt, 
  FaSearch, 
  FaTimes, 
  FaPhone, 
  FaClock, 
  FaStore,
  FaMapPin,
  FaGlobe,
  FaStar,
  FaUsers,
  FaShieldAlt,
  FaCalendarAlt
} from 'react-icons/fa';
import api from './api'

export default function OurStores() {
  const [storeCities, setStoreCities] = useState([]);
  const [storeLocations, setStoreLocations] = useState([]);
  const [selectedCity, setSelectedCity] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredStores, setFilteredStores] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const citiesScrollRef = useRef(null);

  const cityCoordinates = {
    'Delhi': { lat: 28.6139, lng: 77.2090 },
    'Mumbai': { lat: 19.0760, lng: 72.8777 },
    'Bangalore': { lat: 12.9716, lng: 77.5946 },
    'Noida': { lat: 28.5355, lng: 77.3910 },
    'Gurgaon': { lat: 28.4595, lng: 77.0266 },
    'Pune': { lat: 18.5204, lng: 73.8567 },
    'Hyderabad': { lat: 17.3850, lng: 78.4867 },
    'Chennai': { lat: 13.0827, lng: 80.2707 },
    'Kolkata': { lat: 22.5726, lng: 88.3639 },
    'Ahmedabad': { lat: 23.0225, lng: 72.5714 },
    'Jaipur': { lat: 26.9124, lng: 75.7873 },
    'Lucknow': { lat: 26.8467, lng: 80.9462 },
    'Kanpur': { lat: 26.4499, lng: 80.3319 },
    'Patna': { lat: 25.5941, lng: 85.1376 },
    'Ludhiana': { lat: 30.9010, lng: 75.8573 },
    'Jalandhar': { lat: 31.3260, lng: 75.5762 },
    'Ghaziabad': { lat: 28.6692, lng: 77.4538 }
  };

  useEffect(() => {
    fetchCities();
    fetchStoreLocations();
  }, []);

  useEffect(() => {
    if (storeCities.length > 0 && storeLocations.length > 0 && !selectedCity && !searchTerm) {
      const noidaCity = storeCities.find(city => city.name.toLowerCase() === 'noida');
      
      if (noidaCity) {
        setSelectedCity('Noida');
        setSearchTerm('Noida');
        
        const filtered = storeLocations.filter(store => store.city_id === noidaCity.id);
        setFilteredStores(filtered);
      }
    }
  }, [storeCities, storeLocations]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.search-wrapper_3')) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const fetchCities = async () => {
    try {
      const res = await api.get('/cities');
      if (res?.data?.data) {
        setStoreCities(res.data.data);
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
    }
  };

  const fetchStoreLocations = async () => {
    try {
      setLoading(true);
      const res = await api.get('/getStoreLocations');
      if (res?.data?.data) {
        setStoreLocations(res.data.data);
        setFilteredStores([]);
      }
    } catch (error) {
      console.error("Error fetching store locations:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCitySearch = (term) => {
    setSearchTerm(term);
    setShowDropdown(true);
    if (term.length === 0) {
      setFilteredStores([]);
      setSelectedCity('');
    }
  };

  const handleSearchFocus = () => {
    setShowDropdown(true);
  };

  const handleCitySelect = (city) => {
    setSelectedCity(city);
    setSearchTerm(city);
    setShowDropdown(false);
    
    // Find the city object to get its ID
    const cityData = storeCities.find(c => c.name === city);
    if (cityData) {
      const filtered = storeLocations.filter(store => store.city_id === cityData.id);
      setFilteredStores(filtered);
    } else {
      setFilteredStores([]);
    }
    
    setTimeout(() => {
      const storesSection = document.querySelector('.city-results-section_3');
      if (storesSection) {
        const elementPosition = storesSection.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - 80;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 150);
  };

  const clearSelection = () => {
    setSelectedCity('');
    setSearchTerm('');
    setFilteredStores([]);
    setShowDropdown(false);
    setTimeout(() => {
      const searchInput = document.querySelector('.search-input_3');
      if (searchInput) {
        searchInput.focus();
      }
    }, 100);
  };

  const getFilteredCities = () => {
    if (searchTerm.length === 0) {
      return storeCities;
    }
    return storeCities.filter(city => 
      city.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const scrollCitiesLeft = () => {
    if (citiesScrollRef.current) {
      citiesScrollRef.current.scrollBy({ left: -400, behavior: 'smooth' });
    }
  };

  const scrollCitiesRight = () => {
    if (citiesScrollRef.current) {
      citiesScrollRef.current.scrollBy({ left: 400, behavior: 'smooth' });
    }
  };

  return (
    <div className="stores-page_3">
      <div className="hero-section_3">
        <div className="hero-background_3">
          <div className="hero-pattern_3"></div>
          <div className="hero-gradient_3"></div>
        </div>
        <div className="hero-content_3">
          <div className="hero-text_3">
            <h1>Find Our Stores</h1>
            <p>Discover premium NeatnClean locations across India</p>
            <div className="hero-stats_3">
              <div className="hero-stat_3">
                <span className="stat-number_3">{storeCities.length}+</span>
                <span className="stat-label_3">Cities</span>
              </div>
              <div className="hero-stat_3">
                <span className="stat-number_3">{storeLocations.length}+</span>
                <span className="stat-label_3">Stores</span>
              </div>
              <div className="hero-stat_3">
                <span className="stat-number_3">24/7</span>
                <span className="stat-label_3">Service</span>
              </div>
            </div>
          </div>
          <div className="hero-visual_3">
            <div className="hero-icon_3">
              <FaMapPin />
            </div>
          </div>
        </div>
      </div>

      <div className="cities-section_3">
        <div className="cities-container_3">
          <div className="cities-header_3">
            <h2>Our Service Cities</h2>
            <p>Discover NeatnClean locations in major cities across India</p>
          </div>
          <div className="cities-scroll-container_3">
            <button className="scroll-btn_3 left_3" onClick={scrollCitiesLeft}>
              <FaChevronLeft />
            </button>
            <div className="cities-grid-scroll_3" ref={citiesScrollRef}>
              {storeCities.map((city, idx) => {
                const cityStoreCount = storeLocations.filter(store => store.city_id === city.id).length;
                return (
                  <div 
                    key={idx} 
                    className={`city-card_3 ${selectedCity === city.name ? 'selected_3' : ''}`}
                    onClick={() => handleCitySelect(city.name)}
                  >
                    <div className="city-image_3">
                      <img src={city.img} alt={city.name} />
                      <div className="city-overlay_3">
                        <div className="city-badge_3">
                          <FaStore />
                          <span>{cityStoreCount} stores</span>
                        </div>
                      </div>
                    </div>
                    <div className="city-info_3">
                      <h3>{city.name}</h3>
                      <div className="city-stats_3">
                        <div className="city-stat_3">
                          <FaMapPin />
                          <span>{cityStoreCount} Locations</span>
                        </div>
                        <div className="city-stat_3">
                          <FaClock />
                          <span>24/7 Service</span>
                        </div>
                      </div>
                      <div className="city-action_3">
                        <span>View Stores</span>
                        <FaChevronRight />
                        <div className="scroll-indicator_3">
                          <span>↓</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <button className="scroll-btn_3 right_3" onClick={scrollCitiesRight}>
              <FaChevronRight />
            </button>
          </div>
        </div>
      </div>
      
      <div className="search-section_3">
        <div className="search-container_3">
          <div className="search-header_3">
            <h2>Find Store By City</h2>
            <p>Search by city name to discover NeatnClean locations near you</p>
          </div>
          <div className="search-wrapper_3">
            <div className="search-input-container_3">
              <FaSearch className="search-icon_3" />
              <input
                type="text"
                placeholder="Enter city name (e.g., Delhi, Mumbai, Bangalore)..."
                value={searchTerm}
                onChange={(e) => handleCitySearch(e.target.value)}
                onFocus={handleSearchFocus}
                className="search-input_3"
              />
              {searchTerm && (
                <button onClick={clearSelection} className="clear-btn_3">
                  <FaTimes />
                </button>
              )}
            </div>
            
            {showDropdown && (
              <div className="dropdown_3">
                {searchTerm.length === 0 && (
                  <div className="dropdown-header_3">
                    <FaMapPin className="header-icon_3" />
                    <span>Select a city to view stores</span>
                  </div>
                )}
                {getFilteredCities().length > 0 ? (
                  getFilteredCities().map((city, idx) => (
                    <div
                      key={idx}
                      className="dropdown-item_3"
                      onClick={() => handleCitySelect(city.name)}
                    >
                      <FaMapPin className="item-icon_3" />
                      <div className="item-content_3">
                        <span className="item-name_3">{city.name}</span>
                        <span className="item-stores_3">
                          {storeLocations.filter(store => store.city_id === city.id).length} stores
                        </span>
                      </div>
                    </div>
                  ))
                ) : searchTerm.length > 0 ? (
                  <div className="dropdown-item_3 no-results_3">
                    <FaTimes className="item-icon_3" />
                    <span>No cities found for "{searchTerm}"</span>
                  </div>
                ) : null}
              </div>
            )}
          </div>
          
          <div className="search-features_3">
            <div className="feature-item_3">
              <FaClock className="feature-icon_3" />
              <span>Quick Search</span>
            </div>
            <div className="feature-item_3">
              <FaMapMarkerAlt className="feature-icon_3" />
              <span>Accurate Locations</span>
            </div>
            <div className="feature-item_3">
              <FaStore className="feature-icon_3" />
              <span>Store Details</span>
            </div>
          </div>
        </div>
      </div>

      {selectedCity && (
        <div className="city-results-section_3">
          <div className="city-results-container_3">
            <div className="city-info-column_3">
              <div className="city-info-card_3">
                <div className="city-image-container_3">
                  {(() => {
                    const cityData = storeCities.find(city => city.name === selectedCity);
                    return cityData ? (
                      <img src={cityData.img} alt={selectedCity} className="city-main-image_3" />
                    ) : (
                      <div className="city-placeholder_3">
                        <FaMapPin className="placeholder-icon_3" />
                        <span>{selectedCity}</span>
                      </div>
                    );
                  })()}
                </div>
                
                <div className="city-content_3">
                  <h2 className="city-title_3">{selectedCity}</h2>
                  <div className="city-stats_3">
                    <div className="stat-item_3">
                      <div className="stat-number_3">{filteredStores.length}</div>
                      <div className="stat-label_3">Store Locations</div>
                    </div>
                    <div className="stat-item_3">
                      <div className="stat-number_3">24/7</div>
                      <div className="stat-label_3">Service Available</div>
                    </div>
                  </div>
                  
                  <div className="city-description_3">
                    <h3>Our Presence in {selectedCity}</h3>
                    <p>
                      NeatnClean has established a strong presence in {selectedCity}, 
                      providing premium laundry and dry cleaning services to residents and businesses. 
                      Our strategically located stores ensure convenient access to professional cleaning services.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="stores-column_3">
              <div className="stores-header_3">
                <h2>Store Locations</h2>
                <span className="store-count_3">{filteredStores.length} stores</span>
              </div>

              {loading ? (
                <div className="loading-container_3">
                  <div className="loading-spinner_3">
                    <div className="spinner-ring_3"></div>
                    <div className="spinner-ring_3"></div>
                    <div className="spinner-ring_3"></div>
                  </div>
                  <h3>Loading Stores</h3>
                  <p>Please wait while we fetch the latest store information...</p>
                </div>
              ) : (
                <div className="stores-list_3">
                  {filteredStores.length > 0 ? (
                    filteredStores.map((store, idx) => (
                      <div key={idx} className="store-item_3">
                        <div className="store-item-header_3">
                          <div className="store-item-icon_3">
                            <FaStore />
                          </div>
                          <div className="store-item-info_3">
                            <h3>{store.store_name || 'NeatnClean Store'}</h3>
                            <p className="store-item-address_3">
                              <FaMapMarkerAlt />
                              {store.address}
                            </p>
                            <div className="store-badge_3">
                              <span className="badge-text_3">Premium Service</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="store-item-details_3">
                          <div className="detail-group_3">
                            <div className="store-detail-row_3">
                              <FaShieldAlt className="detail-icon_3" />
                              <span>Quality Guarantee</span>
                            </div>
                            <div className="store-detail-row_3">
                              <FaClock className="detail-icon_3" />
                              <span>9:00 AM - 8:00 PM</span>
                            </div>
                          </div>
                          
                          <div className="detail-group_3">
                            <div className="store-detail-row_3">
                              <FaGlobe className="detail-icon_3" />
                              <span>Online Booking Available</span>
                            </div>
                            <div className="store-detail-row_3">
                              <FaStore className="detail-icon_3" />
                              <span>Free Pickup & Delivery</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-stores_3">
                      <div className="no-stores-icon_3">
                        <FaStore />
                      </div>
                      <h3>No stores found in {selectedCity}</h3>
                      <p>Try searching for a different city or check our other locations</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="map-section_3">
        <div className="map-container_3">
          <div className="map-header_3">
            <h2>Our Presence Across India</h2>
            <p>Explore our nationwide network of premium laundry and dry cleaning services</p>
          </div>
          <div className="map-wrapper_3">
            <div className="map-container-inner_3">
              <GoogleMap 
                cities={storeCities.map(city => ({
                  name: city.name,
                  lat: cityCoordinates[city.name]?.lat || 20.5937,
                  lng: cityCoordinates[city.name]?.lng || 78.9629
                }))}
                onCityClick={handleCitySelect}
              />
            </div>
            <div className="map-controls_3">
              <div className="map-legend_3">
                <div className="legend-item_3">
                  <div className="legend-pin_3"></div>
                  <span>Store Locations</span>
                </div>
                <div className="legend-item_3">
                  <div className="legend-city_3"></div>
                  <span>Service Cities</span>
                </div>
              </div>
              <div className="map-info_3">
                <div className="info-item_3">
                  <FaMapPin className="info-icon_3" />
                  <span>Click on any city to view stores</span>
                </div>
                <div className="info-item_3">
                  <FaStore className="info-icon_3" />
                  <span>Premium services available</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <FloatingOrderButton />
    </div>
  );
}
